import { PayloadAction, Slice, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ErrorResponse } from "../models/ErrorResponse";
import { getAvailableDates, getAvailableTimes,submitCaptureDetailsForm,createBooking, cancelBookingRequest, branchesLookup,  activeReasonsLookup, availableReasonsLookup } from "./API";
import { Booking, CaptureDetailsResponse, CreateBookingRequest, CreateBookingResponse } from "../models/BookingDetails";
import { CaptureDetailsState } from "./CaptureDetailsSlice";
import { DropDownOption } from "../models/DropDownOption";
import { FormState } from "final-form";
import { ActiveReasons, ActiveReasonsResponse, AllAvailableReasonsResponse, AvailableBranchReasons } from "../models/BookingReasons";
import { Branch, BranchesResponse } from "../models/Branches";
import { Docs } from "../models/RequiredDocs";
import { AvailableDaysResponse, AvailableTimeResponse, SlotsRequest } from "../models/Slots";
import { CaptchaDetailsForm } from "../models/CaptureDetailsForm";
import { PAGES } from "../Components/CreateBooking/CreateBookingWrapper";

export interface BookingState {
  page: number,
  bookings: Array<Booking>,
  dates: AvailableDaysResponse[],
  datesLoading: boolean,
  times: { morning: Array<string>, afternoon: Array<string> },
  displayBookings: boolean,
  bookingsLoading: boolean,
  bookingErrors: null | ErrorResponse,
  createBookingFormState?: null | FormState<Record<string, any>, Partial<Record<string, any>>>,
  requiredFiles: Array<Docs>,
  selectedDate: string,
  selectedTime: string,
  bookingTypes: Array<DropDownOption>,
  branches: Array<DropDownOption>,
  regions: Array<DropDownOption>,
  reasonCategories: Array<DropDownOption>,
  reasons: Array<DropDownOption>,
  branchesWithRegions: Array<Branch>,
  availableReasons: AvailableBranchReasons[],
  filteredAvailableReasons: AvailableBranchReasons[],
  activeReasons: ActiveReasons[],
  bookingTaxpayerID: string,
  canCreateBookings: boolean,
  detailsSubmitted: boolean,
  detailsSubmissionStarted: boolean,
  createBookingSubmissionStarted: boolean,
  cancelBookingSubmissionStarted: boolean,
  bookingCreatedSuccessfully: boolean,
  landingDataLoaded: boolean[],
  timesDataLoading: boolean,
  documentsStillRequired: boolean,
  uploadingDocsForCreatedBooking: boolean,
  taxpayerRegion: string,
  requestorType: string,
  contactCenterNumber: string,
  maskedTaxrefList?: string[],
  displayMaskedTaxrefModal: boolean
}

const initialState: BookingState = {
  page: 0,
  bookings: [],
  dates: [],
  datesLoading: false,
  times: { morning: [], afternoon: [] },
  displayBookings: true,
  bookingsLoading: false,
  bookingErrors: null,
  requiredFiles: [],
  selectedDate: '',
  selectedTime: '',
  bookingTypes: [],
  branches: [],
  regions: [],
  reasonCategories: [],
  reasons: [],
  branchesWithRegions: [],
  availableReasons: [],
  filteredAvailableReasons: [],
  activeReasons: [],
  bookingTaxpayerID: '',
  canCreateBookings: true,
  detailsSubmitted: false,
  detailsSubmissionStarted: false,
  createBookingSubmissionStarted: false,
  cancelBookingSubmissionStarted: false,
  bookingCreatedSuccessfully: false,
  landingDataLoaded: [],
  timesDataLoading: false,
  documentsStillRequired: false,
  uploadingDocsForCreatedBooking: false,
  taxpayerRegion: '',
  requestorType: '',
  contactCenterNumber: '',
  displayMaskedTaxrefModal: false,
};

export const getAllAvailableDates = createAsyncThunk<AvailableDaysResponse[], SlotsRequest, {rejectValue: ErrorResponse}>(
  "booking/getAvailableDates",
  async (data, { rejectWithValue, fulfillWithValue }) => {
    return getAvailableDates(data)
      .then((res) => fulfillWithValue(res))
      .catch((e) => rejectWithValue(e));
  }
);

export const getAllAvailableTimes = createAsyncThunk<AvailableTimeResponse, SlotsRequest, {rejectValue: ErrorResponse}>(
  "booking/getAllAvailableTimes",
  async (data, { rejectWithValue, fulfillWithValue }) => {
    return getAvailableTimes(data)
      .then((res) => fulfillWithValue(res))
      .catch((e) => rejectWithValue(e));
  }
);

export const getBranches = createAsyncThunk<BranchesResponse, void, { rejectValue:ErrorResponse}>(
  "booking/getBranches",
  async (_, { rejectWithValue, fulfillWithValue }) => {
    return branchesLookup()
      .then((res) => fulfillWithValue(res))
      .catch((e: ErrorResponse) => rejectWithValue(e));
  }
);

export const getActiveReasons = createAsyncThunk<ActiveReasonsResponse, void, { state: {booking: BookingState}, rejectValue:ErrorResponse}>(
  "booking/getActiveReasons",
  async (_, { rejectWithValue, fulfillWithValue, getState }) => {
    return activeReasonsLookup(getState().booking.requestorType)
      .then((res) => fulfillWithValue(res))
      .catch((e: ErrorResponse) => rejectWithValue(e));
  }
);

export const getAvailableReasons = createAsyncThunk<AllAvailableReasonsResponse, void, { state: {booking: BookingState}, rejectValue:ErrorResponse}>(
  "booking/getAvailableReasons",
  async (_, { rejectWithValue, fulfillWithValue, getState }) => {
    return availableReasonsLookup(getState().booking.taxpayerRegion)
      .then((res) => fulfillWithValue(res))
      .catch((e: ErrorResponse) => rejectWithValue(e));
  }
);

export const submitCaptureDetailsFormAsync: any = createAsyncThunk<{res: CaptureDetailsResponse, data: any}, void, { state: {captureDetails: CaptureDetailsState}, rejectValue: ErrorResponse}>(
  "booking/submitCaptureDetailsForm",
  async (data, { rejectWithValue, fulfillWithValue, getState }) => {
    return submitCaptureDetailsForm(
      getState().captureDetails.requesterDetailsFormState?.values as CaptchaDetailsForm
    )
    .then((res) => fulfillWithValue({res, data}))
    .catch((e) => rejectWithValue(e));
  }
);

export const createBookingAsync = createAsyncThunk<CreateBookingResponse, void, { state: {booking: BookingState}, rejectValue: ErrorResponse}>(
  "booking/SubmitEBookingRequest",
  async (_, { rejectWithValue, fulfillWithValue, getState }) => {
    const values = getState().booking.createBookingFormState?.values;
    const data: CreateBookingRequest = {
      bookingType: values?.bookingType ?? null, 
      branch: values?.branch ?? null, 
      reason: values?.reason ?? null, 
      reasonCategory: values?.reasonCategory  ?? null, 
      reasonSummary: values?.reasonSummary  ?? null,
      time: getState().booking.selectedTime, 
      date: getState().booking.selectedDate,
      bookingTaxpayerID: getState().booking.bookingTaxpayerID,
    };
    
    return createBooking(data)
    .then((res) => fulfillWithValue(res))
    .catch((e) => rejectWithValue(e));
  }
);

export const cancelBookingAsync = createAsyncThunk<Booking[], {cancelReason: string, bookingId: number}, { state: {booking: BookingState}, rejectValue: ErrorResponse}>(
  "/cancelBooking",
  async ({cancelReason, bookingId}, { rejectWithValue, fulfillWithValue, getState }) => {
    const taxPayerID = getState().booking.bookingTaxpayerID;
    return cancelBookingRequest(cancelReason, bookingId, Number(taxPayerID))
    .then((res) => fulfillWithValue(res))
    .catch((e) => rejectWithValue(e));
  }
);

export const bookingSlice: Slice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    updateCreateBookingFormState: (state: BookingState, action) => {
      state.createBookingFormState = action.payload;
    },
    setPage: (state: BookingState, action) => {
      state.page = action.payload;
    },
    setBookings: (state: BookingState, action) => {
      state.bookings = action.payload;
    },
    setDisplayBookings: (state: BookingState, action) => {
      state.displayBookings = action.payload;
    },
    cancelBooking: (state: BookingState, action) => {
      state.bookingsLoading = false;
      state.bookingErrors = null;
    },
    clearBookingErrors: (state: BookingState, action) => {
      state.bookingErrors = null;
    },
    setBookingErrors: (state: BookingState, action) => {
      state.bookingErrors = action.payload;
    },
    setBookingLoading: (state: BookingState, action) => {
      state.bookingsLoading = action.payload;
    },
    setSelectedDate: (state: BookingState, action) => {
      state.selectedDate = action.payload;
    },
    setSelectedTime: (state: BookingState, action) => {
      state.selectedTime = action.payload;
    },
    setDetailsSubmissionStarted: (state: BookingState, action) => {
      state.detailsSubmissionStarted = action.payload;
    },
    setCreateBookingSubmissionStarted: (state: BookingState, action) => {
      state.createBookingSubmissionStarted = action.payload;
    },
    setCancelBookingSubmissionStarted: (state: BookingState, action) => {
      state.cancelBookingSubmissionStarted = action.payload;
    },
    setTimesDataLoading: (state: BookingState, action) => {
      state.timesDataLoading = action.payload;
    },
    setDates: (state: BookingState, action) => {
      state.dates = action.payload;
    },
    setTimes: (state: BookingState, action) => {
      state.times = action.payload;
    },
    setBookingCreatedSuccessfully: (state: BookingState, action) => {
      state.bookingCreatedSuccessfully = action.payload;
    },
    setDocumentsStillRequired: (state: BookingState, action) => {
      state.documentsStillRequired = action.payload;
    },
    setUploadingDocsForCreatedBooking: (state: BookingState, action) => {
      state.uploadingDocsForCreatedBooking = action.payload;
    },
    setDisplayMaskedTaxrefModal: (state: BookingState, action) => {
      state.displayMaskedTaxrefModal = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
    .addCase(getAllAvailableDates.pending, (state: BookingState) => {
      state.datesLoading = true;
    })
    .addCase(getAllAvailableDates.fulfilled, (state: BookingState, action) => {
      state.dates = [...action.payload];
      state.datesLoading = false;
    })
    .addCase(getAllAvailableDates.rejected, (state: BookingState, action) => {
      state.datesLoading = false;
      state.bookingErrors = (action as PayloadAction<ErrorResponse>).payload;
    })
    .addCase(getAllAvailableTimes.pending, (state: BookingState) => {
      state.bookingsLoading = true
    })
    .addCase(getAllAvailableTimes.fulfilled, (state: BookingState, action: any) => {
      state.times = action.payload;
      state.timesDataLoading = false;
    })
    .addCase(getAllAvailableTimes.rejected, (state: BookingState, action: any) => {
      state.bookingErrors = (action as PayloadAction<ErrorResponse>).payload;
      state.timesDataLoading = false;
    })
    .addCase(getBranches.fulfilled, (state: BookingState, action) => {
      state.branches = action.payload.branches;
      state.regions = action.payload.regions;
      state.branchesWithRegions = action.payload.branchesWithRegions;
      state.landingDataLoaded.push(true);
    })
    .addCase(getBranches.rejected, (state: BookingState, action) => {
      state.bookingErrors = (action as PayloadAction<ErrorResponse>).payload;
      state.landingDataLoaded.push(true);
    })
    .addCase(getActiveReasons.fulfilled, (state: BookingState, action) => {
      state.reasons = action.payload.reasons;
      state.reasonCategories = action.payload.reasonCategories;
      state.bookingTypes = action.payload.bookingTypes;
      state.activeReasons = action.payload.active;
      state.landingDataLoaded.push(true);
    })
    .addCase(getActiveReasons.rejected, (state: BookingState, action) => {
      state.bookingErrors = (action as PayloadAction<ErrorResponse>).payload;
      state.landingDataLoaded.push(true);
    })
    .addCase(getAvailableReasons.fulfilled, (state: BookingState, action) => {
      state.availableReasons = [...action.payload.allAvailableReasons];
      state.filteredAvailableReasons = [...action.payload.filteredAvailableReasons];
      state.landingDataLoaded.push(true);
    })
    .addCase(getAvailableReasons.rejected, (state: BookingState, action) => {
      state.bookingErrors = (action as PayloadAction<ErrorResponse>).payload;
      state.landingDataLoaded.push(true);
    })
    .addCase(submitCaptureDetailsFormAsync.pending, (state: BookingState) => {
      state.bookingsLoading = true
    })
    .addCase(submitCaptureDetailsFormAsync.fulfilled, (state: BookingState, action: PayloadAction<{res: CaptureDetailsResponse, data: any}>) => {
      state.bookings = action.payload.res.response.CurrentBookings ?? [];
      state.bookingTaxpayerID = action.payload.res.response.BookingTaxpayerID.toString();
      state.canCreateBookings = action.payload.res.response.AllowNewBooking === 0;
      state.detailsSubmitted = true; 
      state.detailsSubmissionStarted = false;
      state.taxpayerRegion = action.payload.res.response.BookingTaxpayerRegion;
      state.requestorType = action.payload.res.response.RequestorType;
      state.requiredFiles = [...action.payload.res.requiredDocs ?? []];
      state.maskedTaxrefList = action.payload.res.response.MaskedTaxrefList;

      if(action.payload.res.response.ContactCenterNumber && action.payload.res.response.ContactCenterNumber !== '') {
        state.contactCenterNumber = action.payload.res.response.ContactCenterNumber;
      }

      if (!action.payload.res.response.MaskedTaxrefList)
      {
        if(!action.payload.res.response.CurrentBookings || action.payload.res.response.CurrentBookings.length <= 0) {
          state.page = PAGES.CreateBooking;
          state.displayBookings = false;
        } else {
          state.displayBookings = true;
          state.canCreateBookings = false;
          state.selectedDate = '';
          state.selectedTime = '';
        }
      } else {
        state.displayMaskedTaxrefModal = true;
        state.detailsSubmissionStarted = false;
        state.detailsSubmitted = false;
      }

      if(action.payload.data?.confirmation) {
        state.createBookingFormState = null;
      }
    })
    .addCase(submitCaptureDetailsFormAsync.rejected, (state: BookingState, action) => {
      state.bookingsLoading = false;
      state.detailsSubmissionStarted = false;
      state.bookingErrors = (action as PayloadAction<ErrorResponse>).payload;
      state.detailsSubmitted = false;
    })
    .addCase(createBookingAsync.pending, (state: BookingState) => {
      state.bookingsLoading = true
    })
    .addCase(createBookingAsync.fulfilled, (state: BookingState, action: PayloadAction<CreateBookingResponse>) => {
      state.bookings = [...state.bookings ?? [], ...action.payload.currentBookings ?? []];
      state.requiredFiles = [...action.payload.requiredDocs ?? []];
      state.bookingCreatedSuccessfully = true;
      state.createBookingSubmissionStarted = false;
      state.uploadingDocsForCreatedBooking = false;
    })
    .addCase(createBookingAsync.rejected, (state: BookingState, action) => {
      state.bookingsLoading = false;
      state.bookingErrors = (action as PayloadAction<ErrorResponse>).payload;
      state.bookingCreatedSuccessfully = false;
      state.createBookingSubmissionStarted = false;
      state.uploadingDocsForCreatedBooking = false;

      if(action.payload?.error && action.payload?.error === "The selected day and time slot are no longer available. Please select a different day or time.") {
        state.selectedDate = '';
        state.selectedTime = '';
        state.times = { morning: [], afternoon: [] };
      }
    })
    .addCase(cancelBookingAsync.fulfilled, (state: BookingState, action) => {
      const cancelledBooking = action.payload[0];
      const index = state.bookings.findIndex((booking: Booking) => booking.BookingID === cancelledBooking.BookingID);
      if(index > -1)
      {
        const bookings = state.bookings;
        bookings.splice(index, 1);
        state.bookings = [...bookings];
      }

      if(state.bookings.length <= 0) {
        state.canCreateBookings = true;
      }
      state.bookingCreatedSuccessfully = false;
      state.page = 0;
      state.cancelBookingSubmissionStarted = false;
      state.uploadingDocsForCreatedBooking = false;
    })
    .addCase(cancelBookingAsync.rejected, (state: BookingState, action) => {
      state.bookingErrors = (action as PayloadAction<ErrorResponse>).payload;
      state.bookingCreatedSuccessfully = false;
      state.displayBookings = true;
      state.page = 0;
      state.cancelBookingSubmissionStarted = false;
      state.uploadingDocsForCreatedBooking = false;
    });
  },
});

export const { setPage, 
  setDisplayBookings,
  cancelBooking, 
  clearBookingErrors, 
  setBookingErrors, 
  setBookingLoading,
  updateCreateBookingFormState,
  setSelectedDate,
  setSelectedTime,
  setDetailsSubmissionStarted,
  setCreateBookingSubmissionStarted,
  setCancelBookingSubmissionStarted,
  setTimesDataLoading,
  setDates,
  setTimes,
  setBookingCreatedSuccessfully,
  setDocumentsStillRequired,
  setUploadingDocsForCreatedBooking,
  setDisplayMaskedTaxrefModal
} = bookingSlice.actions;

export const currentPage = (state: { booking: BookingState }) => state?.booking?.page;
export const displayBookings = (state: { booking: BookingState }) => state?.booking?.displayBookings;
export const bookings = (state: { booking: BookingState }) => state?.booking?.bookings;
export const availableDates = (state: { booking: BookingState }) => state?.booking?.dates;
export const availableTimes = (state: { booking: BookingState }) => state?.booking?.times;
export const availableDatesLoading = (state: { booking: BookingState }) => state?.booking?.datesLoading;
export const isBookingLoading = (state: { booking: BookingState }) => state?.booking?.bookingsLoading;
export const bookingErrors = (state: { booking: BookingState }) => state?.booking?.bookingErrors;
export const createBookingFormState = (state: { booking: BookingState }) => state?.booking?.createBookingFormState;
export const requiredFiles = (state: { booking: BookingState }) => state?.booking?.requiredFiles;
export const selectedDate = (state: { booking: BookingState }) => state?.booking?.selectedDate;
export const selectedTime = (state: { booking: BookingState }) => state?.booking?.selectedTime;
export const branches = (state: { booking: BookingState }) => state?.booking?.branches;
export const regions = (state: { booking: BookingState }) => state?.booking?.regions;
export const branchesWithRegions = (state: { booking: BookingState }) => state?.booking?.branchesWithRegions;
export const bookingTypes = (state: { booking: BookingState }) => state?.booking?.bookingTypes;
export const reasons = (state: { booking: BookingState }) => state?.booking?.reasons;
export const reasonCategories = (state: { booking: BookingState }) => state?.booking?.reasonCategories;
export const availableReasons = (state: { booking: BookingState }) => state?.booking?.availableReasons;
export const filteredAvailableReasons = (state: { booking: BookingState }) => state?.booking?.filteredAvailableReasons;
export const activeReasons = (state: { booking: BookingState }) => state?.booking?.activeReasons;
export const bookingTaxpayerID = (state: { booking: BookingState }) => state?.booking?.bookingTaxpayerID;
export const detailsSubmitted = (state: { booking: BookingState }) => state?.booking?.detailsSubmitted;
export const canCreateBooking = (state: { booking: BookingState }) => state?.booking?.canCreateBookings;
export const bookingCreatedSuccessfully = (state: { booking: BookingState }) => state?.booking?.bookingCreatedSuccessfully;
export const landingDataLoaded = (state: { booking: BookingState }) => state?.booking?.landingDataLoaded;
export const detailsSubmissionStarted = (state: { booking: BookingState }) => state?.booking?.detailsSubmissionStarted;
export const timesDataLoading = (state: { booking: BookingState }) => state?.booking?.timesDataLoading;
export const createBookingSubmissionStarted = (state: { booking: BookingState }) => state?.booking?.createBookingSubmissionStarted;
export const cancelBookingSubmissionStarted = (state: { booking: BookingState }) => state?.booking?.cancelBookingSubmissionStarted;
export const documentsStillRequired = (state: { booking: BookingState }) => state?.booking?.documentsStillRequired;
export const uploadingDocsForCreatedBooking = (state: { booking: BookingState }) => state?.booking?.uploadingDocsForCreatedBooking;
export const taxpayerRegion = (state: { booking: BookingState }) => state?.booking?.taxpayerRegion;
export const contactCenterNumber = (state: { booking: BookingState }) => state?.booking?.contactCenterNumber;
export const displayMaskedTaxrefModal = (state: { booking: BookingState }) => state?.booking.displayMaskedTaxrefModal;
export const maskedTaxrefList = (state: { booking: BookingState }) => state?.booking?.maskedTaxrefList;

export default bookingSlice.reducer;
